import { useCallback, useEffect, useRef, useState } from "react"
import TagManager from "react-gtm-module"
import { IoCloseOutline } from "react-icons/io5"
import { LuMoveLeft } from "react-icons/lu"
import { useLocation, useNavigate } from "react-router-dom"

import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import { blurKeys } from "@fuse/sharedComponents/blurWrapper/blurKeysList"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import {
  showErrorToast,
  showInfoToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import { TWebUser } from "@letshang/constants"
import * as Sentry from "@sentry/react"
import { isEmpty } from "lodash-es"
import { useRecoilState } from "recoil"
import { ROUTES } from "src/App"

import {
  screenState,
  updateUserStateFromBackend,
  userState,
} from "@fuse/recoil/signup"

import {
  ELogAnalyticEvent,
  ELogGTMEvent,
  logAnalyticEvent,
} from "@firebaseConfig/analyticsUtil"

import { ensureLetsHangHttps } from "@fuse/utils/common"

import { useSignup } from "@fuse/pages/signUp/controller"
import Dob from "@fuse/pages/signUp/steps/Dob"
import Gender from "@fuse/pages/signUp/steps/Gender"
import Name from "@fuse/pages/signUp/steps/Name"
import Phone from "@fuse/pages/signUp/steps/Phone"
import VerifyOtp from "@fuse/pages/signUp/steps/VerifyOtp"

import { useRecaptcha } from "./RecaptchaStateProvider"

const SignUpComp = () => {
  const { state } = useLocation()
  const { clearRecaptchaToken, initializeTheRecaptchaVerifier } = useRecaptcha()

  useEffect(() => {
    // Send custom event to GTM
    TagManager.dataLayer({
      dataLayer: {
        event: ELogGTMEvent.PAGE_VIEW,
        page_name: "Sign up",
        page_title: document.title,
        page_path: window.location.pathname,
        page_referrer: document.referrer,
        page_url: window.location.href,
        page_url_query: window.location.search,
      },
    })
  }, [])

  const recaptchaRef = useRef(null)

  const reInitializeTheRecaptchaVerifier = useCallback(() => {
    initializeTheRecaptchaVerifier(recaptchaRef)
  }, [initializeTheRecaptchaVerifier])

  useEffect(() => {
    if (recaptchaRef?.current) {
      initializeTheRecaptchaVerifier(recaptchaRef)
    }

    return () => {
      clearRecaptchaToken()
    }
  }, [recaptchaRef?.current])

  const screenMap: Record<number, React.ReactNode> = {
    1: (
      <Phone
        reInitializeTheRecaptchaVerifier={reInitializeTheRecaptchaVerifier}
      />
    ),
    2: (
      <VerifyOtp
        reInitializeTheRecaptchaVerifier={reInitializeTheRecaptchaVerifier}
        redirectTo={state?.redirectTo}
        template={state?.template}
        category={state?.templateCategory}
      />
    ),
    3: <Name />,
    4: <Gender />,
    5: <Dob template={state?.template} />,
  }

  const isRedirectedFromCreateEveV3 =
    state?.redirectTo === ROUTES.CREATE_EVENT_V3

  const urlMap: Record<number, string> = {
    1: "/signup/enter-phone",
    2: "/signup/verify-otp",
    3: "/signup/enter-name",
    4: "/signup/enter-gender",
    5: `/signup/enter-dob${
      state?.redirectTo === ROUTES.CREATE_EVENT
        ? `?action=create-event`
        : state?.redirectTo === ROUTES.CREATE_EVENT_V3
          ? `?action=create-event-v3`
          : state?.redirectTo
            ? `?action=${state?.redirectTo.replaceAll("/", "")}`
            : ""
    }`,
  }

  const navigate = useNavigate()
  const location = useLocation()
  const { isLoading, updateUser } = useSignup()
  const [step, setStep] = useRecoilState(screenState)
  const [user, setUser] = useRecoilState(userState)

  const queryParams = new URLSearchParams(location.search)
  const referenceFrom = queryParams.get("reference")

  useEffect(() => {
    if (!user.phoneNumber) {
      setStep(1)
    }
  }, [])

  const handleBackPress = () => {
    const canGoBack = location.key !== "default"

    if (step <= 1) {
      if (canGoBack) {
        if (
          state?.redirectTo === ROUTES.CREATE_EVENT ||
          state?.redirectTo === ROUTES.CREATE_EVENT_V3 ||
          state?.redirectTo === ROUTES.CREATE_EVENT_V4
        ) {
          if (!isEmpty(referenceFrom)) {
            window.location.href = ensureLetsHangHttps(referenceFrom)
          } else {
            navigate(-1)
          }
        } else {
          navigate(-1)
        }
      } else {
        if (!isEmpty(referenceFrom)) {
          window.location.href = ensureLetsHangHttps(referenceFrom)
        } else {
          window.location.href = "https://letshang.info/"
        }
      }
    } else {
      setStep(step - 1)
    }
  }

  useEffect(() => {
    // change url
    const host = window.location.origin
    const url = window.location.href

    // query params
    const queryParams = url.split("?")[1]

    let newUrl = host.split("?")[0] + urlMap[step]
    // add query params if they exist
    if (queryParams) {
      newUrl += "?" + queryParams
    }
    if (user?.isLoggedIn && state?.redirectTo) {
      window.history.replaceState({}, "", state?.redirectTo)
    } else if (step === 1) {
      window.history.replaceState({}, "", newUrl)
    } else {
      window.history.pushState({}, "", newUrl)
    }
  }, [step])

  const onSkipSignUp = async () => {
    try {
      const userObj = {
        name: user.name || "",
        dateOfBirth: user.dateOfBirth || "",
        gender: user.gender || "",
        expoPushToken: user.expoPushToken || "",
        country: user.country || "",
        phoneNumber: user.phone || "",
      }

      const res: any = await updateUser({ ...userObj, eventId: user.eventId })

      if (res.success) {
        await updateUserStateFromBackend()
        setUser((prev: TWebUser) => ({
          ...prev,
          isOnboarded: true,
          isLoggedIn: true,
        }))

        logAnalyticEvent(ELogAnalyticEvent.SIGN_UP_WEB, {
          uid: user.uid,
          page_name: "Sign up",
          action: "skipped optional fields",
        })

        if (state?.redirectTo) {
          navigate(state.redirectTo, {
            state: {
              template: state?.template || {},
              category: state?.templateCategory,
            },
          })
        } else {
          navigate(user.redirectUrl || "/")
        }
      } else {
        console.error("Error updating user details")
        showErrorToast(res.message)
      }
    } catch (e) {
      console.error(e)
    }
  }

  const HeaderClose: React.FC = () => {
    return (
      <div className="absolute top-12 pl-4 md:top-16 md:pl-12 flex items-center space-x-2 z-10 w-full">
        <div
          onClick={() => {
            if (state?.backToTemplate) {
              navigate(
                `${ROUTES.EVENT_TEMPLATES}?category=${state?.templateCategory}`,
              )
            } else {
              handleBackPress()
            }
          }}
          className="absolute right-4 md:right-12 w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full flex justify-center items-center  bg-white/10 cursor-pointer z-10"
        >
          <IoCloseOutline className="text-white text-md md:text-3xl" />
        </div>
      </div>
    )
  }

  const HeaderBack: React.FC = () => {
    return (
      <div className="absolute top-12 pl-4 md:top-16 md:pl-12 flex items-center space-x-2 z-10 w-full">
        <div
          onClick={handleBackPress}
          className="absolute w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full flex justify-center items-center  bg-white/10 cursor-pointer z-10"
        >
          <LuMoveLeft className="text-white text-md md:text-2xl" />
        </div>
      </div>
    )
  }

  const HeaderSkip: React.FC = () => {
    return (
      <div className="absolute top-12 pl-4 md:top-16 md:pl-12 flex items-center space-x-2 z-10 w-full">
        <div
          onClick={handleBackPress}
          className="absolute w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full flex justify-center items-center  bg-white/10 cursor-pointer z-10"
        >
          <LuMoveLeft className="text-white text-md md:text-2xl" />
        </div>
        <div
          onClick={onSkipSignUp}
          className="absolute right-4 md:right-12 w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 flex justify-center items-center cursor-pointer z-10"
        >
          <p className=" underline text-white/35">Skip</p>
        </div>
      </div>
    )
  }

  const renderHeader = () => {
    switch (step) {
      case 1:
        return <HeaderClose />
      case 2:
        return <HeaderBack />
      case 3:
        return null
      case 4:
        return <HeaderSkip />
      case 5:
        return <HeaderSkip />

      default:
        return null
    }
  }

  return (
    <BackgroundWrapper
      showImage={!isRedirectedFromCreateEveV3}
      bgImage={"/assets/alpha-event-not-found.svg"}
      className={`min-h-screen ${isRedirectedFromCreateEveV3 ? "!bg-gradient-to-b from-[#F1C2DB] to-[#4F5293]" : ""}`}
    >
      <BlurWrapper
        blurKey={blurKeys?.mainBlurView}
        overlayOpacityDefault={isRedirectedFromCreateEveV3 ? "0" : "75"}
        overlayColorDefault="dark"
        className={`h-full w-full min-h-screen flex items-center justify-center ${isRedirectedFromCreateEveV3 ? "bg-black/50" : ""}`}
      >
        <div ref={recaptchaRef} className="recaptcha-container"></div>
        <div className="relative z-[10]">{isLoading && <LoadingSpinner />}</div>
        {renderHeader()}

        {screenMap[step]}
      </BlurWrapper>
    </BackgroundWrapper>
  )
}

export default SignUpComp
